import React, { useEffect } from 'react';
import Navbar from '@/components/landing/Navbar';
import HeroSection from '@/components/landing/HeroSection';
import HowItWorksSection from '@/components/landing/HowItWorksSection';
import FeaturePillars from '@/components/landing/FeaturePillars';
import SecuritySection from '@/components/landing/SecuritySection';
import PersonaCTAs from '@/components/landing/PersonaCTAs';
import ROIStrip from '@/components/landing/ROIStrip';
import PricingPreview from '@/components/landing/PricingPreview';
import Footer from '@/components/landing/Footer';
import PartnerLandingPage from '@/components/landing/PartnerLandingPage';
import { useBranding } from '../helpers/useBranding';

import './landing.module.css';

const structuredData = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Tasking.tech',
    url: 'https://tasking.tech',
    description:
      'Tasking.tech is an agentic AI SaaS platform that deploys autonomous AI agents for task automation, project management, and intelligent workflow orchestration.',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Tasking.tech',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: 'https://tasking.tech',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    featureList: [
      'Autonomous AI agents for task execution',
      'Agentic workflow automation',
      'AI-powered project management',
      'Multi-agent orchestration with human oversight',
      'Skill injection and custom AI capabilities',
      'Enterprise governance and audit trails',
    ],
    description:
      'Deploy autonomous AI agents that plan, execute, and deliver work with built-in governance, human approvals, and full audit trails.',
  },
]);

const Index = () => {
  const { branding } = useBranding();

  useEffect(() => {
    // Enable scrolling for landing page by overriding #root overflow
    const root = document.getElementById('root');
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      document.body.style.overflow = 'auto';
    }
    return () => {
      // Reset on unmount — clear inline styles so global.css defaults take over
      if (root) {
        root.style.overflow = '';
        root.style.height = '';
        document.body.style.overflow = '';
      }
    };
  }, []);

  // Partner/white-label subdomains get their own branded landing page
  if (branding.source !== 'default') {
    return <PartnerLandingPage branding={branding} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground overflow-auto">
      <>
        <title>
          Tasking.tech | Agentic AI Platform — Autonomous Agents for Teams
        </title>
        <meta
          name="description"
          content="Tasking.tech is the agentic AI SaaS platform where autonomous AI agents plan, execute, and deliver work with human oversight, governance, and full audit trails."
        />
        <meta
          name="keywords"
          content="agentic AI platform, AI agent SaaS, autonomous AI agents, AI task automation, agentic workflow automation, multi-agent orchestration, AI-powered project management, TaskingBot, enterprise AI agents"
        />
        <meta property="og:title" content="Tasking.tech | Agentic AI Platform for Teams" />
        <meta
          property="og:description"
          content="Deploy autonomous AI agents that plan, build, test, and ship — with human approvals and enterprise governance built in."
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tasking.tech" />
        <meta property="og:url" content="https://tasking.tech" />
        <meta property="og:image" content="https://tasking.tech/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tasking.tech | Agentic AI Platform for Teams" />
        <meta
          name="twitter:description"
          content="Deploy autonomous AI agents for task automation, project delivery, and intelligent workflows with enterprise-grade security."
        />
        <meta name="twitter:image" content="https://tasking.tech/og-image.png" />
        <link rel="canonical" href="https://tasking.tech" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: structuredData }} />
      </>

      <Navbar />
      <HeroSection />
      <HowItWorksSection />
      <FeaturePillars />
      <SecuritySection />
      <PersonaCTAs />
      <ROIStrip />
      <PricingPreview />
      <Footer />
    </div>
  );
};

export default Index;
